import React, { useState, useEffect, useCallback } from 'react';
import { Package, Check, X, Trash2, Clock, DollarSign, TrendingUp, AlertCircle, RefreshCw, Search } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from '../../utils/translationHelpers';
import './VendorDashboard.css';

const VendorDashboard = ({ user, language }) => {
  const { t } = useTranslation(language);
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [cancelModal, setCancelModal] = useState({ show: false, coupon: null, confirmation: '' });
  const [redeemModal, setRedeemModal] = useState({ show: false, coupon: null });
  const [searchTerm, setSearchTerm] = useState('');

  const vendorName = user.role === 'speedos' ? 'Speedos' : 'Ryder';

  const fetchCoupons = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/vendor/vendor/coupons${filter ? `?status=${filter}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      toast.error(t('failedToLoadCoupons', 'Failed to load coupons'));
    }
  }, [filter]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/vendor/vendor/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCoupons(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchCoupons, fetchStats]);

  const confirmRedeem = async () => {
    if (!redeemModal.coupon) return;
    try {
      const token = localStorage.getItem("token");
      await axios.put(`/api/vendor/vendor/coupons/${redeemModal.coupon._id}/redeem`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("couponRedeemed", "Coupon redeemed successfully"));
      setRedeemModal({ show: false, coupon: null });
      fetchCoupons();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || t("failedToRedeem", "Failed to redeem coupon"));
    }
  };

  const handleCancel = async () => {
    if (cancelModal.confirmation !== 'Cancel') {
      toast.error(t('pleaseTypeCancel', 'Please type "Cancel" to confirm'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/vendor/vendor/coupons/${cancelModal.coupon._id}/cancel`,
        { confirmation: 'Cancel' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('couponCancelled', 'Coupon cancelled and points refunded'));
      setCancelModal({ show: false, coupon: null, confirmation: '' });
      fetchCoupons();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || t('failedToCancel', 'Failed to cancel coupon'));
    }
  };

  const handleDelete = async (couponId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/vendor/vendor/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('couponDeleted', 'Coupon deleted'));
      fetchCoupons();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || t('failedToDelete', 'Failed to delete coupon'));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'redeemed': return 'status-redeemed';
      case 'confirmed': return 'status-confirmed';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock size={14} />;
      case 'redeemed': return <Check size={14} />;
      case 'confirmed': return <Check size={14} />;
      case 'cancelled': return <X size={14} />;
      default: return null;
    }
  };

  const filteredCoupons = (coupons || []).filter(coupon => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      coupon.username?.toLowerCase().includes(term) ||
      coupon.packageCategory?.toLowerCase().includes(term) ||
      coupon._id.toLowerCase().includes(term) || coupon.couponCode?.toLowerCase().includes(term)
    );
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="vendor-dashboard loading">
        <RefreshCw className="spin" size={24} />
        <span>{t('loading', 'Loading...')}</span>
      </div>
    );
  }

  return (
    <div className="vendor-dashboard">
      <div className="vendor-header">
        <div className="vendor-title">
          <Package size={24} />
          <h2>{vendorName} {t('dashboard', 'Dashboard')}</h2>
        </div>
        <button className="refresh-btn" onClick={() => { fetchCoupons(); fetchStats(); }}>
          <RefreshCw size={16} />
          {t('refresh', 'Refresh')}
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="vendor-stats">
          <div className="stat-card pending">
            <Clock size={20} />
            <div className="stat-info">
              <span className="stat-value">{stats.pending?.count || 0}</span>
              <span className="stat-label">{t('pending', 'Pending')}</span>
            </div>
          </div>
          <div className="stat-card redeemed">
            <Check size={20} />
            <div className="stat-info">
              <span className="stat-value">{stats.redeemed?.count || 0}</span>
              <span className="stat-label">{t('redeemed', 'Redeemed')}</span>
            </div>
          </div>

          <div className="stat-card owed">
            <DollarSign size={20} />
            <div className="stat-info">
              <span className="stat-value">{stats.owed?.toFixed(0) || 0} ILS</span>
              <span className="stat-label">{t("owed", "Owed")}</span>
            </div>
          </div>
          <div className="stat-card total">
            <TrendingUp size={20} />
            <div className="stat-info">
              <span className="stat-value">{stats.paid?.toFixed(0) || 0} ILS</span>
              <span className="stat-label">{t('paid', 'Paid')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="vendor-filters">
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            <Clock size={14} />
            {t('pending', 'Pending')}
          </button>
          <button
            className={`filter-tab ${filter === 'redeemed' ? 'active' : ''}`}
            onClick={() => setFilter('redeemed')}
          >
            <Check size={14} />
            {t('redeemed', 'Redeemed')}
          </button>

          <button
            className={`filter-tab ${filter === 'cancelled' ? 'active' : ''}`}
            onClick={() => setFilter('cancelled')}
          >
            <X size={14} />
            {t('cancelled', 'Cancelled')}
          </button>
          <button
            className={`filter-tab ${filter === '' ? 'active' : ''}`}
            onClick={() => setFilter('')}
          >
            {t('all', 'All')}
          </button>
        </div>
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder={t('searchCoupons', 'Search coupons...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Coupons List */}
      <div className="coupons-list">
        {filteredCoupons.length === 0 ? (
          <div className="no-coupons">
            <AlertCircle size={32} />
            <p>{t('noCouponsFound', 'No coupons found')}</p>
          </div>
        ) : (
          filteredCoupons.map(coupon => (
            <div key={coupon._id} className={`coupon-card ${getStatusColor(coupon.status)}`}>
              <div className="coupon-header">
                <div className="coupon-user">
                  <strong>{coupon.username || coupon.user?.username || coupon.user?.inGameName || 'Unknown'}</strong>
                  <span className={`status-badge ${getStatusColor(coupon.status)}`}>
                    {getStatusIcon(coupon.status)}
                    {coupon.status.charAt(0).toUpperCase() + coupon.status.slice(1)}
                  </span>
                </div>
                <div className="coupon-date">{formatDate(coupon.createdAt)}</div>
              </div>
              <div className="coupon-details">
                <div className="coupon-package">
                  <Package size={14} />
                  <span>{coupon.packageCategory}</span>
                </div>
                <div className="coupon-price">
                  <DollarSign size={14} />
                  <span>{coupon.priceILS} ILS</span>
                </div>
                <div className="coupon-points">
                  <span>{coupon.pointsDeducted} {t('pts', 'pts')}</span>
                </div>
              {coupon.couponCode && (
                <div className="coupon-code" style={{marginTop: "8px", padding: "6px 10px", background: "rgba(167, 139, 250, 0.2)", borderRadius: "6px", display: "inline-block"}}>
                  <span style={{color: "#a78bfa", fontWeight: "600", fontFamily: "monospace", fontSize: "14px"}}>{coupon.couponCode}</span>
                </div>
              )}
              </div>
              <div className="coupon-actions">
                {coupon.status === 'pending' && (
                  <>
                    <button
                      className="action-btn redeem"
                      onClick={() => setRedeemModal({ show: true, coupon })}
                    >
                      <Check size={14} />
                      {t('redeem', 'Redeem')}
                    </button>
                    <button
                      className="action-btn cancel"
                      onClick={() => setCancelModal({ show: true, coupon, confirmation: '' })}
                    >
                      <X size={14} />
                      {t('cancel', 'Cancel')}
                    </button>
                  </>
                )}
                {['cancelled', 'confirmed'].includes(coupon.status) && (
                  <button
                    className="action-btn delete"
                    onClick={() => handleDelete(coupon._id)}
                  >
                    <Trash2 size={14} />
                    {t('delete', 'Delete')}
                  </button>
                )}
                {false && coupon.status === 'redeemed' && (
                  <span className="waiting-confirmation">
                    <Clock size={14} />
                    {t('waitingAdminConfirmation', 'Waiting for admin confirmation')}
                  </span>
                )}
              </div>
              {coupon.status === 'cancelled' && coupon.pointsRefunded && (
                <div className="refund-notice">
                  <AlertCircle size={12} />
                  {t('pointsRefunded', 'Points refunded to user')}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {cancelModal.show && (
        <div className="modal-overlay" onClick={() => setCancelModal({ show: false, coupon: null, confirmation: '' })}>
          <div className="modal-content cancel-modal" onClick={e => e.stopPropagation()}>
            <h3>{t('cancelCoupon', 'Cancel Coupon')}</h3>
            <p>{t('cancelWarning', 'This will refund all points to the user. This action cannot be undone.')}</p>
            <div className="coupon-summary">
              <p><strong>{t('user', 'User')}:</strong> {cancelModal.coupon?.username}</p>
              <p><strong>{t('package', 'Package')}:</strong> {cancelModal.coupon?.packageCategory}</p>
              <p><strong>{t('refundAmount', 'Refund Amount')}:</strong> {cancelModal.coupon?.pointsDeducted} points</p>
            </div>
            <div className="confirmation-input">
              <label>{t('typeCancel', 'Type "Cancel" to confirm')}:</label>
              <input
                type="text"
                value={cancelModal.confirmation}
                onChange={(e) => setCancelModal({ ...cancelModal, confirmation: e.target.value })}
                placeholder="Cancel"
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setCancelModal({ show: false, coupon: null, confirmation: '' })}
              >
                {t('back', 'Back')}
              </button>
              <button
                className="btn-danger"
                onClick={handleCancel}
                disabled={cancelModal.confirmation !== 'Cancel'}
              >
                {t('confirmCancel', 'Confirm Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Redeem Confirmation Modal */}
      {redeemModal.show && (
        <div className="modal-overlay" onClick={() => setRedeemModal({ show: false, coupon: null })}>
          <div className="modal-content cancel-modal" onClick={e => e.stopPropagation()}>
            <h3>{t('confirmRedeem', 'Confirm Redeem')}</h3>
            <p>{t('redeemConfirmText', 'Are you sure you want to mark this coupon as redeemed?')}</p>
            <div className="coupon-summary">
              <p><strong>{t('user', 'User')}:</strong> {redeemModal.coupon?.username}</p>
              <p><strong>{t('package', 'Package')}:</strong> {redeemModal.coupon?.packageCategory}</p>
              <p><strong>{t('amount', 'Amount')}:</strong> {redeemModal.coupon?.priceILS} ILS</p>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setRedeemModal({ show: false, coupon: null })}>
                {t('no', 'No')}
              </button>
              <button className="btn-primary" onClick={confirmRedeem} style={{ background: '#059669' }}>
                {t('yesRedeem', 'Yes, Redeem')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorDashboard;
